import { useTranslation } from 'react-i18next';
import { UserAttribution, type UserAttributionSummary } from '../ui/UserAttribution';
import { formatListingTimestamp } from '../../utils/timezone';
import { formatPickupMoment, formatTimeWindow, presentWindow } from '../../utils/timeWindow';
import type { TimeWindow } from '../../types/listing';

interface OrderDetailCardProps {
  orderNumber: string;
  quantity: number;
  /** Enum value (e.g. "pickup") — translated via `card.fulfillment.{value}`. */
  fulfillmentMethod: string;
  /** ISO wall-clock string. Rendered in the listing's timezone as day + time
   *  ("Tomorrow 5:00 pm", "April 12th, 5:00 pm") — a bare clock reading is
   *  ambiguous the moment an order isn't for today. */
  pickupTime?: string;
  /** Drop-off only: the chef's drop-off window. A drop-off has no picked time,
   *  so the row states the window it arrives in instead of `pickupTime`. */
  dropoffWindow?: TimeWindow | null;
  /** Absolute ISO instant. Formatted in the listing's timezone as "MM/DD/YY H:MM AM/PM TZ". */
  orderedAt: string;
  /** Listing timezone (from `listing.timeWindow.timezone`). Falls back to
   *  the viewer's browser timezone when absent — legacy rows may lack one. */
  timezone?: string | null;
  /** Total row value — pass "Free" for zero-dollar completed orders, or the
   *  chef's gross earnings for a paid order (chef view), or the eater's total
   *  paid (eater view). Kept as ReactNode so callers can format however. */
  total: React.ReactNode;
  /**
   * Optional counterparty banner at the top of the card — Avatar + name +
   * connection-level badge. Chef view passes the buyer (matches the chef
   * order-activity card pattern, `size="md"` per that surface). Eater view
   * OMITS this (the buyer is the viewer — no attribution to show), which
   * preserves the pre-existing eater card layout exactly.
   */
  userAttribution?: UserAttributionSummary | null;
}

/**
 * The rounded, muted-bg card at the bottom of the eater's order-detail page
 * — now shared with the chef's per-order rows on the listing detail page.
 *
 * Row spacing is `space-y-1.5` (half of the old inline value). The Ordered
 * timestamp uses `formatListingTimestamp` so both eater and chef see the
 * moment in the listing's own timezone, not the viewer's browser zone —
 * that keeps pickup context coherent (a chef in Denver still sees the Vail
 * pickup time in Vail's clock).
 *
 * Deliberately not aware of order status / role. Callers decide what to put
 * in the Total slot (Free, dollars, gross) and whether to show the card at
 * all. Keeps the component reusable and free of business rules.
 */
export const OrderDetailCard: React.FC<OrderDetailCardProps> = ({
  orderNumber,
  quantity,
  fulfillmentMethod,
  pickupTime,
  dropoffWindow,
  orderedAt,
  timezone,
  total,
  userAttribution,
}) => {
  const { t } = useTranslation('orders');
  // Resolved in the LISTING's zone, so both the clock time and the DAY are
  // the ones that apply where the food is, not wherever the viewer happens
  // to be. Same source of truth the feed card and portion sheet use.
  const pickupMoment = formatPickupMoment(pickupTime, timezone);
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-1.5 text-sm">
      {userAttribution && (
        <div className="mb-2">
          <UserAttribution
            summary={userAttribution}
            size="md"
            nameClassName="text-sm font-medium"
            interactive
          />
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t('inProgress.details.orderNumber')}</span>
        <span>{orderNumber}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t('inProgress.details.quantity')}</span>
        <span>{quantity}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t('inProgress.details.fulfillment')}</span>
        <span>
          {t(`card.fulfillment.${fulfillmentMethod}` as const, {
            defaultValue: fulfillmentMethod,
          })}
        </span>
      </div>
      {fulfillmentMethod === 'dropoff' && presentWindow(dropoffWindow) ? (
        // A drop-off arrives somewhere in the chef's window, not at a picked
        // time, so state the window.
        <div className="flex items-start justify-between gap-3">
          <span className="text-muted-foreground">
            {t('inProgress.details.dropoffTime')}
          </span>
          <span className="text-right font-medium">{formatTimeWindow(presentWindow(dropoffWindow))}</span>
        </div>
      ) : (
        pickupMoment && (
          // `gap-3` and a right-aligned value so a long label and a long day +
          // time value don't collide at large text scales.
          <div className="flex items-start justify-between gap-3">
            <span className="text-muted-foreground">
              {t(
                fulfillmentMethod === 'dropoff'
                  ? 'inProgress.details.dropoffTime'
                  : 'inProgress.details.pickupTime',
              )}
            </span>
            <span className="text-right font-medium">{pickupMoment.full}</span>
          </div>
        )
      )}
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t('inProgress.details.ordered')}</span>
        <span>{formatListingTimestamp(orderedAt, timezone)}</span>
      </div>
      <div className="flex items-center justify-between font-semibold border-t border-border pt-1.5">
        <span>{t('inProgress.details.total')}</span>
        <span>{total}</span>
      </div>
    </div>
  );
};
